import type { Wallet as TonConnectWallet } from "@tonconnect/ui-react";
import type { CreateWalletRequest } from "../../../shared/api/wallet";
import type { Wallet } from "../../../entities/wallet";

type TonConnectAccountLike = {
  address?: string;
  addresses?: string[];
  publicKey?: string;
  public_key?: string;
  chain?: number | string;
  walletVersion?: string;
  wallet_version?: string;
  walletStateInit?: string;
  wallet_state_init?: string;
};

export interface WalletRegistrationPayload {
  rawAddress: string;
  request: CreateWalletRequest;
}

export const resolveNetwork = (
  chain: unknown,
): CreateWalletRequest["network"] => {
  if (typeof chain === "number") {
    return chain === -3 ? "TESTNET" : "MAINNET";
  }

  if (typeof chain === "string") {
    const normalized = chain.toLowerCase();
    if (normalized.includes("test")) return "TESTNET";
    if (normalized.includes("-3")) return "TESTNET";
  }

  return "MAINNET";
};

export const resolveWalletVersion = (
  value: unknown,
): CreateWalletRequest["walletVersion"] => {
  if (typeof value !== "string") return "OTHER";
  const normalized = value.toUpperCase();
  if (normalized.includes("V4")) return "V4R2";
  if (normalized.includes("V3")) return "V3R2";
  if (normalized.includes("SAFE")) return "SAFE_MULTISIG";
  if (normalized.includes("HIGHLOAD")) return "HIGHLOAD_V2";
  return "OTHER";
};

export const normalizeAddress = (input: unknown): string | null => {
  if (typeof input === "string" && input.length > 0) return input;
  if (Array.isArray(input) && input.length > 0) {
    const first = input.find(item => typeof item === "string");
    if (first) return first;
  }
  return null;
};

export const normalizePublicKey = (input: unknown): string | null => {
  if (typeof input === "string" && input.length >= 64) return input;
  return null;
};

export const normalizeStateInit = (input: unknown): string | undefined => {
  if (typeof input === "string" && input.length > 0) return input;
  return undefined;
};

const buildMetadata = (
  wallet: TonConnectWallet,
): Record<string, unknown> | undefined => {
  const entries = (
    [
      ["appName", wallet.device?.appName],
      ["appVersion", wallet.device?.appVersion],
      ["platform", wallet.device?.platform],
      ["provider", wallet.provider],
    ] as const
  ).filter(([, value]) => value !== undefined && value !== null && `${value}`.length > 0);

  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(entries);
};

export const walletMatches = (
  stored: Wallet,
  incomingAddress: string,
) => {
  const normalizedIncoming = incomingAddress.toLowerCase().trim();
  
  // Normalize by removing workchain prefix if present (e.g., "0:abc..." -> "abc...")
  const normalizeAddress = (addr: string) => {
    // Remove workchain prefix (e.g., "0:", "-1:", etc.)
    const withoutPrefix = addr.replace(/^[-]?[0-9]+:/, '').toLowerCase();
    return withoutPrefix;
  };
  
  const normalizedIncomingNoPrefix = normalizeAddress(normalizedIncoming);
  const storedFriendlyNoPrefix = normalizeAddress(stored.friendlyAddress);
  const storedRawNoPrefix = normalizeAddress(stored.rawAddress);
  
  return (
    stored.friendlyAddress.toLowerCase() === normalizedIncoming ||
    stored.rawAddress.toLowerCase() === normalizedIncoming ||
    storedFriendlyNoPrefix === normalizedIncomingNoPrefix ||
    storedRawNoPrefix === normalizedIncomingNoPrefix
  );
};

export const deriveWalletRegistrationPayload = (
  wallet: TonConnectWallet,
): WalletRegistrationPayload | null => {
  const account = (wallet.account ?? {}) as TonConnectAccountLike;
  const rawAddress =
    normalizeAddress(account.address) ?? normalizeAddress(account.addresses);

  if (!rawAddress) {
    return null;
  }

  const publicKey =
    normalizePublicKey(account.publicKey) ??
    normalizePublicKey(account.public_key);

  if (!publicKey) {
    return null;
  }

  const network = resolveNetwork(account.chain);
  const walletVersion = resolveWalletVersion(
    account.walletVersion ?? account.wallet_version,
  );
  const stateInit =
    normalizeStateInit(account.walletStateInit) ??
    normalizeStateInit(account.wallet_state_init);

  const metadata = buildMetadata(wallet);

  const request: CreateWalletRequest = {
    network,
    address: rawAddress,
    walletVersion,
    publicKeyHex: publicKey,
    stateInitB64: stateInit,
    label: wallet.device?.appName ?? "Wallet",
    metadata,
  };

  return {
    rawAddress,
    request,
  };
};

